// POSSO store configuration — the editable layer until Shopify connects.
// Price/dispatch/copy/FAQs/legal all edit here. When Shopify connects,
// price/stock/variants come from Shopify; this file keeps brand copy.

import { PRIVACY_EMAIL } from "./pogoConfig";

export const STORE = {
  preorderStatus: "PREORDER — FIRST PRODUCTION RUN",
  // EDIT: replace with the final dispatch date/window before launch.
  // Obvious placeholder — do not invent a date.
  dispatchWindow: "15 Dec",
  reassurance: ["Secure checkout", "Clear cancellation policy", "Dispatch shown before purchase"],
  heroReassurance: "PREORDERS NOW OPEN • AUSTRALIAN LAUNCH",
  contactEmail: PRIVACY_EMAIL,
};

export const SOCIAL_PROOF = {
  heading: "Worn on real shifts.",
  body: "POSSO is being wear-tested with nurses and healthcare workers before launch. Real tester feedback, photos and reviews land here as the first run ships — nothing fake, ever.",
  placeholders: [
    "WEAR-TEST VIDEO — SLOT RESERVED",
    "TESTER PHOTOS — SLOT RESERVED",
    "REVIEWS + RATINGS — SLOT RESERVED",
  ],
};

// Short policy answers under "How preorder works" — editable placeholders
// until final policies are supplied. Do not invent terms.
export const POLICY_NOTES = [
  { q: "When am I charged?", a: "At checkout, when you place your preorder. Not before." },
  { q: "Can I change my size?", a: "[POLICY PLACEHOLDER] — size-change terms confirmed before checkout opens." },
  { q: "Can I change my colour?", a: "[POLICY PLACEHOLDER] — colour-change terms confirmed before checkout opens." },
  { q: "Can I cancel?", a: "[POLICY PLACEHOLDER] — cancellation terms confirmed before checkout opens." },
  { q: "What if production is delayed?", a: "[POLICY PLACEHOLDER] — we'll email updates and your options if timelines move." },
];

// Grouped around real purchase objections. [PLACEHOLDER] answers are
// editable stand-ins until final policies/specs are supplied.
// Landing FAQ — trimmed to the six purchase-critical questions so the
// section stays scannable. (The PDP pulls its own PRODUCT_FAQS from
// config/product.js.)
export const LANDING_FAQS = [
  { q: "How does POSSO sizing work?", a: "POSSO is listed in European (EU) women's sizing — 35 to 42. Use the size guide on the product; full fit recommendations land before preorder opens." },
  { q: "What does preorder mean?", a: "You pay now, before stock arrives. Your pair is made in the first production run and shipped to you as soon as it's ready." },
  { q: "When will my POSSOs ship?", a: "Estimated dispatch: 15th of December — they'll be waiting under the Christmas tree. Secure your pair now 💗 Preorder now to lock in your POSSO clogs before our official launch. First drop quantities are limited! You'll be notified once we ship." },
  { q: "When am I charged?", a: "At checkout, when you place your preorder. Not before." },
  { q: "Can I cancel my preorder?", a: "[POLICY PLACEHOLDER] — cancellation terms confirmed before checkout opens." },
  { q: "What is the return policy?", a: "We offer one size exchange per order within 30 days of delivery — shoes clean, unworn outdoors and in their original packaging. Full details on our Returns & Exchanges page (link in the footer)." },
];

export const LEGAL_SHIPPING = `Free shipping Australia-wide on every order.

Estimated dispatch: 15th of December — they'll be waiting under the Christmas tree. You'll be notified once we ship. Delivery estimates are shown in your order confirmation.`;

export const LEGAL_TERMS = `Full terms of sale will be published here before checkout opens.

The short version: preorders are charged at checkout. You can cancel any time before dispatch for a full refund. Dispatch estimates are always shown before you pay.`;

// Full Privacy Policy (owner-supplied, 25 Oct 2026). Structured so the
// crawlable /privacy page and the in-context modal render identical copy
// with proper section headings.
export const PRIVACY_POLICY = {
  updated: "25 October 2026",
  intro:
    "At POSSO, we respect your privacy and are committed to handling your personal information responsibly. This Privacy Policy explains what information we collect, how we use it, and how you can contact us about your information.",
  sections: [
    {
      h: "1. Information We Collect",
      paras: [
        "When you visit our website, join our mailing list or place an order, we may collect:",
      ],
      list: [
        "Your name, email address and phone number.",
        "Your shipping and billing addresses.",
        "Your selected shoe colours, sizes and preferences.",
        "Your order details, purchase history and customer service enquiries.",
        "Device information, browser type, approximate location, website interactions and referral information.",
        "Advertising attribution information, including UTM parameters and advertising click identifiers.",
      ],
      after: [
        "Payment information is processed through our ecommerce payment providers. POSSO does not directly store your full payment card details.",
      ],
    },
    {
      h: "2. How We Use Your Information",
      paras: ["We use your information to:"],
      list: [
        "Process and fulfil orders and preorders.",
        "Provide order confirmations, shipping updates and customer support.",
        "Manage product availability and customer preferences.",
        "Improve our website, products and shopping experience.",
        "Measure advertising performance and website engagement.",
        "Send promotional emails or SMS messages where you have consented.",
        "Meet applicable legal and business obligations.",
      ],
    },
    {
      h: "3. Cookies and Analytics",
      paras: [
        "Our website uses cookies and similar technologies to support website functionality, understand visitor behaviour and measure advertising performance.",
        "We use services including:",
      ],
      list: [
        "Microsoft Clarity: Helps us understand how visitors interact with our website through session recordings, heatmaps, clicks, scrolling and other interaction data. Clarity may collect device and browser information and use cookies or similar technologies. We configure masking to reduce the collection of sensitive personal information.",
        "Meta (Facebook and Instagram): Helps us measure advertising performance, understand conversions and deliver relevant advertising.",
        "Shopify: Supports our ecommerce storefront, shopping cart, checkout and order processing.",
      ],
      after: [
        "These providers may process information according to their own privacy policies.",
      ],
    },
    {
      h: "4. Email and SMS Marketing",
      paras: [
        "If you subscribe to POSSO marketing communications, we may send you product announcements, launch updates, promotions and other marketing information.",
        "You can unsubscribe from marketing emails using the unsubscribe link in our emails or opt out of marketing SMS by replying STOP.",
        "Transactional messages relating to existing orders may still be sent where necessary.",
      ],
    },
    {
      h: "5. Sharing Your Information",
      paras: [
        "We may share information with trusted service providers where necessary to operate our business, including:",
      ],
      list: [
        "Shopify and payment processors.",
        "Klaviyo for email and SMS communications.",
        "Microsoft Clarity and Meta for analytics and advertising.",
        "Warehousing, fulfilment and delivery partners.",
        "Technology providers supporting website operations and security.",
      ],
      after: [
        "We do not sell customer information as a standalone product. Some advertising technologies may involve sharing data for targeted advertising purposes.",
      ],
    },
    {
      h: "6. Overseas Processing",
      paras: [
        "Some of our technology and service providers may store or process information outside Australia.",
        "Where applicable, we take reasonable steps to protect personal information shared with overseas service providers.",
      ],
    },
    {
      h: "7. Data Security and Retention",
      paras: [
        "We take reasonable measures to protect personal information against misuse, loss, unauthorised access, modification and disclosure.",
        "We retain personal information for as long as reasonably necessary for the purposes described in this policy, including fulfilling orders, providing customer support and meeting applicable legal obligations.",
      ],
    },
    {
      h: "8. Access, Correction and Deletion",
      paras: [
        "You can contact us to request access to, correction of, or deletion of your personal information.",
        "We will respond to requests in accordance with applicable laws. Certain information may need to be retained for legal, accounting or operational purposes.",
      ],
    },
    {
      h: "9. Your Privacy Choices",
      paras: [
        "You can manage or disable certain cookies through your browser settings. Disabling cookies may affect some website functionality.",
        "You can also contact us about your marketing preferences or privacy concerns.",
      ],
    },
    {
      h: "10. Changes to This Policy",
      paras: [
        "We may update this Privacy Policy as our business, technology or legal obligations change.",
        "The latest version will be available on our website.",
      ],
    },
    {
      h: "11. Contact Us",
      paras: ["For privacy questions, requests or complaints, contact:"],
      list: ["POSSO", `Email: ${PRIVACY_EMAIL}`, "Website: https://www.wearposso.com.au/"],
    },
  ],
};

